import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/i18n/I18nContext";
import { useLanguageSettings } from "@/hooks/useLanguageSettings";
import {
  TRI_PREFERRED_LANG_GROUPS,
  LANG_LABELS,
} from "@/lib/i18n/tri-language";

/**
 * Phase 8–10 — preferred language (Indian + global) + Sanskrit/English display toggles.
 */
export function TriLanguageSettings() {
  const { preferredLanguage, showEnglish, showSanskrit, setLanguageSettings } = useLanguageSettings();
  const { t } = useI18n();

  return (
    <Card className="bg-white/90 shadow-card mb-8" data-testid="tri-language-settings">
      <CardHeader>
        <CardTitle className="font-heading text-lg">{t("tri.settings_title") || "Language & meanings"}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {t("tri.settings_hint") ||
            "Sanskrit and English are always available. Choose your preferred language for meanings — Indian or global."}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="preferred-lang">{t("nav.language") || "Preferred language"}</Label>
          <select
            id="preferred-lang"
            className="mt-1 w-full border rounded-md px-2 py-2 text-sm bg-white"
            value={preferredLanguage}
            onChange={(e) => setLanguageSettings({ preferredLanguage: e.target.value })}
          >
            <option value="en">{LANG_LABELS.en} (fallback)</option>
            {TRI_PREFERRED_LANG_GROUPS.map((group) => (
              <optgroup
                key={group.id}
                label={
                  group.id === "indian"
                    ? t("tri.indian_languages") || group.label
                    : t("tri.global_languages") || group.label
                }
              >
                {group.codes.map((code) => (
                  <option key={code} value={code}>
                    {LANG_LABELS[code] || code.toUpperCase()}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="show-english">{t("tri.show_english") || "Show English meaning"}</Label>
          <Switch
            id="show-english"
            checked={showEnglish}
            onCheckedChange={(v) => setLanguageSettings({ showEnglish: v })}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="show-sanskrit">{t("tri.show_sanskrit") || "Show Sanskrit & transliteration"}</Label>
          <Switch
            id="show-sanskrit"
            checked={showSanskrit}
            onCheckedChange={(v) => setLanguageSettings({ showSanskrit: v })}
          />
        </div>
      </CardContent>
    </Card>
  );
}
